// /app/frontend/src/CheckReconcilePage.jsx
// Sprint C (Angela 2026-05-18) — Safeguard check reconcile UI
// Linha-a-linha: ✅ Correto / ⚠️ Internal→External / ❌ Contestar
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CheckCircle, AlertTriangle, XCircle, RefreshCw, DollarSign, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TODAY_ISO = new Date().toISOString().slice(0, 10);
const isoMonday = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = d.getUTCDay() || 7;
  if (day !== 1) d.setUTCDate(d.getUTCDate() - day + 1);
  return d.toISOString().slice(0, 10);
};

export default function CheckReconcilePage() {
  const [weekStart, setWeekStart] = useState(isoMonday(TODAY_ISO));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // item_id being resolved
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveReason, setApproveReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!weekStart) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/check-reconcile/${weekStart}`);
      setReport(res.data);
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rerun = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/finance/check-reconcile/run`, { week_start: weekStart });
      await fetchReport();
      setMsg({ type: "success", text: "Reconcile regenerated." });
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Failed" });
    } finally {
      setLoading(false);
    }
  };

  const resolveItem = async (item_id, decision, reason = "") => {
    setBusy(item_id);
    try {
      await axios.post(`${API}/finance/check-reconcile/item/${item_id}/resolve`, {
        week_start: weekStart, decision, reason,
      });
      await fetchReport();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Failed" });
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    if (approveReason.trim().length < 20) {
      setMsg({ type: "error", text: "Reason must be >= 20 chars" });
      return;
    }
    setApproving(true);
    try {
      const res = await axios.post(`${API}/finance/check-reconcile/${weekStart}/approve-and-pay`, {
        confirmation_token: "APPROVE-AND-PAY-CONFIRMED",
        reason: approveReason.trim(),
      });
      setMsg({ type: "success", text: `Approved · ${res.data.generated?.length || 0} inspector payments generated.` });
      setApproveOpen(false);
      setApproveReason("");
      await fetchReport();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Failed" });
    } finally {
      setApproving(false);
    }
  };

  const s = report?.summary || {};
  const items = report?.items || [];
  const isApproved = report?.status === "approved";
  const pendingCount = items.filter(i => !i.resolved && i.classification === "pending").length;

  return (
    <div className="space-y-4 p-4" data-testid="check-reconcile-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#1F2937]">Conferência do Cheque Safeguard</h1>
            <p className="text-xs text-gray-500">Semana {report?.week_start} → {report?.week_end} · Scope cutoff: {report?.scope_cutoff}</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={weekStart}
              onChange={e => setWeekStart(isoMonday(e.target.value))}
              className="px-2 py-1.5 border border-gray-300 rounded text-xs"
              data-testid="check-reconcile-week-input"
            />
            <button onClick={rerun} disabled={loading || isApproved} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded flex items-center gap-1.5 disabled:opacity-50" data-testid="check-reconcile-rerun">
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Recalcular
            </button>
            <button
              onClick={() => setApproveOpen(true)}
              disabled={isApproved || items.length === 0 || pendingCount > 0}
              className="px-3 py-1.5 bg-[#6B8F62] hover:bg-[#5a7a52] text-white text-xs font-bold rounded flex items-center gap-1.5 disabled:bg-gray-300"
              data-testid="check-reconcile-approve-btn"
            >
              <DollarSign className="h-3 w-3" /> {isApproved ? "Aprovado" : "Aprovar & Gerar Pagamentos"}
            </button>
          </div>
        </div>

        {msg && (
          <div className={`p-2.5 rounded text-xs ${msg.type === "error" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`} data-testid="check-reconcile-message">
            {msg.text}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="check-reconcile-summary">
          <SummaryCard label="Closings" value={s.total_closings || 0} color="text-gray-700" />
          <SummaryCard label="Checks" value={s.total_checks_lines || 0} color="text-gray-700" />
          <SummaryCard label="Match" value={s.match || 0} color="text-green-600" />
          <SummaryCard label="Pending" value={s.pending || 0} color="text-amber-600" />
          <SummaryCard label="Flip" value={s.flip || 0} color="text-orange-600" />
          <SummaryCard label="Dispute" value={s.dispute || 0} color="text-red-600" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="check-reconcile-totals">
          <SummaryCard label="Total Safeguard" value={`$${(s.total_sg_amount || 0).toFixed(2)}`} color="text-[#2F5D8A]" />
          <SummaryCard label="Total AARIFI Esperado" value={`$${(s.total_aarifi_amount || 0).toFixed(2)}`} color="text-[#1F2937]" />
          <SummaryCard label="Total Inspetor Líquido" value={`$${(s.total_inspector_net || 0).toFixed(2)}`} color="text-[#6B8F62]" />
          <SummaryCard label="Margem AARIFI" value={`$${(s.total_company_margin || 0).toFixed(2)}`} color="text-[#B08A3E]" />
        </div>

        {/* Items table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="check-reconcile-items">
          {loading && items.length === 0 ? (
            <div className="py-10 flex items-center justify-center text-gray-400 text-xs"><Loader2 className="h-4 w-4 animate-spin mr-2" />Carregando...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-xs text-gray-400">
              Nenhum closing elegível encontrado nesta semana.
              <br />
              <span className="text-[10px]">Scope: somente closings após {report?.scope_cutoff}.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold text-gray-500">Order</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-500">Inspector</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-500">Tipo</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-500">AARIFI</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-500">Cheque</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-500">Diff</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-500">Inspector Net</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-500">Margem</th>
                    <th className="px-2 py-2 text-center font-semibold text-gray-500">Status</th>
                    <th className="px-2 py-2 text-center font-semibold text-gray-500">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.item_id} data-testid={`reconcile-row-${it.order_number}`} className={`border-t border-gray-100 ${it.resolved ? "bg-gray-50/50" : ""}`}>
                      <td className="px-2 py-1.5 font-mono font-semibold">{it.order_number}</td>
                      <td className="px-2 py-1.5">{it.inspector}</td>
                      <td className="px-2 py-1.5 text-gray-500">{it.rate_tier}</td>
                      <td className="px-2 py-1.5 text-right">${(it.aarifi_amount || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">${(it.sg_amount || 0).toFixed(2)}</td>
                      <td className={`px-2 py-1.5 text-right font-semibold ${Math.abs(it.diff || 0) < 0.01 ? "text-gray-400" : "text-red-600"}`}>${(it.diff || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right text-[#6B8F62]">${(it.inspector_net || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right text-[#B08A3E]">${(it.company_margin || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-center">
                        <StatusBadge item={it} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {!it.resolved && !isApproved ? (
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => resolveItem(it.item_id, "correct")} disabled={busy === it.item_id} className="px-1.5 py-0.5 bg-green-100 hover:bg-green-200 text-green-700 text-[10px] font-bold rounded disabled:opacity-50" data-testid={`reconcile-correct-${it.order_number}`} title="Correto">✅</button>
                            <button onClick={() => resolveItem(it.item_id, "flip", "Internal flipped to External")} disabled={busy === it.item_id} className="px-1.5 py-0.5 bg-orange-100 hover:bg-orange-200 text-orange-700 text-[10px] font-bold rounded disabled:opacity-50" data-testid={`reconcile-flip-${it.order_number}`} title="Internal flipped to External">⚠️</button>
                            <button onClick={() => { const r = prompt("Motivo para contestar Safeguard:"); if (r) resolveItem(it.item_id, "dispute", r); }} disabled={busy === it.item_id} className="px-1.5 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 text-[10px] font-bold rounded disabled:opacity-50" data-testid={`reconcile-dispute-${it.order_number}`} title="Contestar Safeguard">❌</button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">{it.decision || (isApproved ? "approved" : "—")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Approve modal */}
        {approveOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1200] p-4" data-testid="check-reconcile-approve-modal">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl p-5 space-y-4">
              <h2 className="text-base font-bold text-[#1F2937]">Aprovar & Gerar Pagamentos</h2>
              <p className="text-xs text-gray-600">
                Esta ação vai gerar <strong>inspector_payments</strong> para a semana {weekStart}. Cada inspetor recebe um registro com status "approved · to_be_paid". Items disputed não geram pagamento.
              </p>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Motivo <span className="text-red-600">(obrigatório, min 20 chars)</span></label>
                <textarea
                  value={approveReason}
                  onChange={e => setApproveReason(e.target.value)}
                  rows={3}
                  placeholder="Ex: Aprovado em conferência semanal — todos os flips revisados, 2 disputas em aberto."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#2F5D8A] focus:outline-none resize-none"
                  data-testid="check-reconcile-approve-reason"
                />
                <p className="text-[10px] text-gray-400 mt-1">{approveReason.length}/20+ chars</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setApproveOpen(false)} disabled={approving} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg" data-testid="check-reconcile-approve-cancel">Cancelar</button>
                <button onClick={approve} disabled={approving || approveReason.trim().length < 20} className="flex-1 py-2 bg-[#6B8F62] hover:bg-[#5a7a52] text-white text-sm font-bold rounded-lg disabled:bg-gray-300" data-testid="check-reconcile-approve-confirm">
                  {approving ? "Aprovando..." : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <p className="text-[9px] uppercase font-bold text-gray-400 tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ item }) {
  if (item.resolved) {
    const d = item.decision;
    if (d === "correct") return <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">✅ correct</span>;
    if (d === "flip") return <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold">⚠️ flip</span>;
    if (d === "dispute") return <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">❌ dispute</span>;
    return <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{d}</span>;
  }
  const cls = item.classification;
  if (cls === "match") return <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">✅ match</span>;
  if (cls === "missing_check") return <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold">⏳ missing check</span>;
  return <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">⚠️ pending</span>;
}
